package com.evoapps.evoshift.photo

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.unit.IntRect
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

/**
 * Where the photo sits under the square crop window: [scale] screen pixels per image pixel and
 * [offset], how far the image's centre is from the window's centre (screen pixels).
 */
data class CropTransform(val scale: Float, val offset: Offset = Offset.Zero)

/**
 * The geometry of the 1:1 crop (SH-94) for an image of [imageWidth]×[imageHeight] under a square
 * window of [viewport] pixels (the circle is drawn inside it). The image always covers the whole
 * window, so the circle can never show an empty corner: zoom stops at "fills the window" and
 * panning stops at the image's edges.
 */
class CropMath(val imageWidth: Int, val imageHeight: Int, val viewport: Float) {
    init {
        require(imageWidth > 0 && imageHeight > 0 && viewport > 0f) { "Empty image or window" }
    }

    /** The smallest scale: the shorter side of the image fills the window. */
    val minScale: Float = viewport / min(imageWidth, imageHeight)

    /** The largest scale: [MAX_ZOOM] times closer than [minScale]. */
    val maxScale: Float = minScale * MAX_ZOOM

    /** The start (and double-tap) position: the whole short side, centred. */
    fun initial(): CropTransform = CropTransform(minScale, Offset.Zero)

    /** [transform] with its scale in range and its offset moved back until the image covers the window. */
    fun clamp(transform: CropTransform): CropTransform {
        val scale = transform.scale.coerceIn(minScale, maxScale)
        val maxX = max(0f, (imageWidth * scale - viewport) / 2f)
        val maxY = max(0f, (imageHeight * scale - viewport) / 2f)
        return CropTransform(scale, Offset(transform.offset.x.coerceIn(-maxX, maxX), transform.offset.y.coerceIn(-maxY, maxY)))
    }

    /** Moves the image by [delta] screen pixels (a drag). */
    fun pan(transform: CropTransform, delta: Offset): CropTransform =
        clamp(transform.copy(offset = transform.offset + delta))

    /**
     * Zooms by [factor] around [focus] (screen pixels from the window's centre: the pinch's
     * centre or the mouse pointer), so the point under the fingers stays under them.
     */
    fun zoom(transform: CropTransform, factor: Float, focus: Offset = Offset.Zero): CropTransform {
        if (factor <= 0f || factor.isNaN()) return transform
        val scale = (transform.scale * factor).coerceIn(minScale, maxScale)
        val applied = scale / transform.scale
        return clamp(CropTransform(scale, (transform.offset - focus) * applied + focus))
    }

    /** The square of the image (image pixels) that the window shows: what is uploaded. */
    fun cropRect(transform: CropTransform): IntRect {
        val t = clamp(transform)
        val shortSide = min(imageWidth, imageHeight)
        val side = (viewport / t.scale).roundToInt().coerceIn(1, shortSide)
        val centreX = imageWidth / 2f - t.offset.x / t.scale
        val centreY = imageHeight / 2f - t.offset.y / t.scale
        val left = (centreX - side / 2f).roundToInt().coerceIn(0, imageWidth - side)
        val top = (centreY - side / 2f).roundToInt().coerceIn(0, imageHeight - side)
        return IntRect(left, top, left + side, top + side)
    }

    companion object {
        /** How far the person can zoom in from "fills the window". */
        const val MAX_ZOOM = 6f

        /** One step of the zoom buttons and of a mouse-wheel notch. */
        const val ZOOM_STEP = 1.25f
    }
}
